/**
 * T.Z24 — the duplicate gate of the «Добавить ребёнка» / «Править строку» drawer
 * (owner, live testing 2026-09-12: «дубликаты надо ограничить»).
 *
 * Pure by the house rule of this folder (see `./cabinet-model`): no transport, no
 * React — the `unit` Playwright project imports this file in plain Node. The
 * transport half (the `?q=` read the gate runs before a write) lives in
 * `../../api/curator`; the drawer (`./PrefillFormDrawer`) decides WHEN to run it.
 *
 * THE RULES, exactly as the owner stated them — every rule is about a row that is
 * ALREADY LIVE in the curator's scope:
 *
 *   1. same ФИО + same номер заявления (pgu_id)          → HARD BLOCK,
 *   2. same ФИО + same регион                            → HARD BLOCK,
 *   3. same ФИО only (region/ids differ or are empty)    → WARNING, «Продолжить»
 *                                                          submits for real,
 *   4. the server's own 409 `CONFLICT` (the natural-key
 *      unique index, migration 0017)                     → the SAME hard-block copy.
 *
 * A hard block never reaches the wire; a warning is only an offer — «Отменить»
 * keeps the drawer as it is, «Продолжить» sends the create/patch the curator
 * already asked for.
 *
 * SCOPE, and why it is scope-based rather than venue-based: the natural key is
 * PER VENUE, so the server alone would let the same child exist once per filial of
 * the same partner — and the owner's complaint is about the same child twice in
 * the CURATOR'S world, not on one venue. The gate therefore reads `#26.2` WITHOUT
 * `venueId` (the whole session scope — the curator's flagged venues, exactly what
 * «Все места» shows) and classifies what comes back. The server's per-venue 409
 * stays the backstop for the race the read cannot see.
 *
 * WHY THE WARNING CARRIES A COUNT AND NOT A VENUE: the projection is contact-free
 * and venue-blind (A12/D9 — `CuratorPrefillRow` has no `venueId`/venue name), so
 * the drawer cannot say WHERE the twin lives. It says how many rows look alike
 * instead; the owner's «venue/rows summary» is answered by that count.
 */

/** The fields of a live row the rules can compare — a structural slice of `CuratorPrefillRow`. */
export interface PrefillDuplicateHit {
  id: number;
  fullName: string;
  region: string | null;
  pguId: string | null;
}

/** What the curator is about to write — the validated draft, already trimmed. */
export interface PrefillDuplicateSubject {
  fullName: string;
  pguId?: string | null;
  region?: string | null;
}

export type PrefillDuplicateVerdict =
  | { kind: 'pass' }
  | { kind: 'block'; reason: 'pgu' | 'region'; message: string }
  | { kind: 'warn'; hits: number; message: string };

/** The narrowed warning — what the drawer parks while it asks the curator «Продолжить?». */
export type PrefillDuplicateWarn = Extract<PrefillDuplicateVerdict, { kind: 'warn' }>;

/** The drawer's hard-block sentence — rule 1/2, and the server 409's copy (rule 4). */
export const PREFILL_DUPLICATE_BLOCK_MESSAGE = 'Такой ребёнок уже добавлен.';

/**
 * Unicode hyphens/dashes that fold to a SPACE — the shared `normalizeFio` family,
 * spelled out in escapes because a literal dash in source is invisible to review.
 */
const HYPHEN_LIKE = /[\u00ad\u2010\u2011\u2012\u2013\u2014\u2015\u2212\ufe58\ufe63\uff0d]/g;
/** Combining marks a copy/paste from Word leaves behind (NFKC does not strip them). */
const MARKS = /\p{M}/gu;
/** Punctuation INSIDE a name token («О'Коннор»). */
const INTRA_WORD_NOISE = /['’`´]/g;
/** One name token: a run of letters in any script. */
const WORD = /\p{L}+/gu;

/**
 * The ФИО match key — the server's `normalizeFio` (`@cb-manager/shared`
 * `address/fio.ts`), restated. A rule that exists in two places is stated in both
 * and tested in both (the same discipline `cabinet-model`'s `isFullFioFormat`
 * already follows): the SPA cannot import the shared value export into the
 * plain-Node unit project, and the wire read (`?q=`) is answered from
 * `full_name_norm`, so both sides of the equality must fold identically.
 *
 * NFKC → hyphen-like to SPACE → lowercase `ru` → `ё → е` → strip marks → drop
 * apostrophes → keep letter runs, space-joined. Idempotent, deterministic, and
 * EXACT-equality only (Р14 — no fuzzy matching of a child, ever).
 */
export function normalizeFioKey(raw: string): string {
  if (typeof raw !== 'string') return '';
  let out = raw.normalize('NFKC');
  out = out.replace(HYPHEN_LIKE, ' ');
  out = out.toLocaleLowerCase('ru');
  out = out.replace(/ё/g, 'е');
  out = out.normalize('NFD').replace(MARKS, '');
  out = out.replace(INTRA_WORD_NOISE, '');
  return (out.match(WORD) ?? []).join(' ');
}

/**
 * The region comparison key. The server's `region_norm` is dictionary-gated (a
 * city like «Кемерово» stores an EMPTY norm), so the client cannot rebuild it —
 * and must not pretend to. This fold is the honest subset: case, `ё`, NFKC and
 * whitespace. Two spellings the fold cannot reconcile («Кемерово» vs
 * «Кемеровская область») fall through to the ФИО-only warning instead of a wrong
 * hard block — the softer side of the mistake, by design.
 */
export function normalizeRegionKey(raw: string | null | undefined): string {
  if (typeof raw !== 'string') return '';
  return raw
    .normalize('NFKC')
    .toLocaleLowerCase('ru')
    .replace(/ё/g, 'е')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * The verdict for one about-to-be-written row against the live rows the scope read
 * answered. `excludeRowId` is the row being EDITED: without it the drawer would
 * warn a curator about the very row they opened (the hit list is the same scope
 * read the row itself came from). It never applies to a CREATE — there is no row
 * to exclude.
 *
 * Rules run strongest-first: a pgu_id twin blocks even when the regions also match,
 * and the warning is what survives when neither identifier agrees.
 */
export function classifyPrefillDuplicates(
  subject: PrefillDuplicateSubject,
  hits: readonly PrefillDuplicateHit[],
  options: { excludeRowId?: number } = {},
): PrefillDuplicateVerdict {
  const subjectFio = normalizeFioKey(subject.fullName);
  // Not a comparable ФИО (an empty form, a format the api would refuse anyway) —
  // the gate stays out of the way; validation and the server own that refusal.
  if (subjectFio === '') return { kind: 'pass' };

  const twins = hits.filter(
    (hit) => hit.id !== options.excludeRowId && normalizeFioKey(hit.fullName) === subjectFio,
  );
  if (twins.length === 0) return { kind: 'pass' };

  const subjectPgu = typeof subject.pguId === 'string' ? subject.pguId.trim() : '';
  if (subjectPgu !== '' && twins.some((hit) => hit.pguId === subjectPgu)) {
    return {
      kind: 'block',
      reason: 'pgu',
      message: `${PREFILL_DUPLICATE_BLOCK_MESSAGE} Строка с этим номером заявления уже есть в вашем списке.`,
    };
  }

  const subjectRegion = normalizeRegionKey(subject.region);
  if (subjectRegion !== '' && twins.some((hit) => normalizeRegionKey(hit.region) === subjectRegion)) {
    return {
      kind: 'block',
      reason: 'region',
      message: `${PREFILL_DUPLICATE_BLOCK_MESSAGE} Строка с таким ФИО и регионом уже есть в вашем списке.`,
    };
  }

  return { kind: 'warn', hits: twins.length, message: duplicateWarningMessage(twins.length) };
}

/** The warning's sentence — «<venue/rows summary>» is the count (see the module doc). */
export function duplicateWarningMessage(count: number): string {
  return `Найден возможный дубликат: в вашем списке уже есть ${ruSimilarRows(count)}. Продолжить?`;
}

/** «1 похожая строка», «2 похожие строки», «5 похожих строк» — RU plural, no library. */
function ruSimilarRows(count: number): string {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return `${count} похожая строка`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${count} похожие строки`;
  return `${count} похожих строк`;
}
